import { Component, inject, input, OnInit } from '@angular/core';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxFileUploaderModule } from '@aposin/ng-aquila/file-uploader';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NxPopoverModule } from '@aposin/ng-aquila/popover';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { TranslateModule } from '@ngx-translate/core';

import { SkeletonComponent } from '#components/skeleton/skeleton.component';
import { environment } from '#environments/environment';
import {
  DocumentMetadata,
  DocumentStatus,
  documentStatusesWithAvailableDownload,
  documentStatusMapping,
  pendingDocumentStatuses,
} from '#models/MicroController.model';
import DocumentService from '#services/document/document.service';
import { formatDate } from '#utils/dateUtils';
import { timer } from '#utils/timer';

@Component({
  selector: 'kyc-document-info',
  imports: [
    NxSpinnerModule,
    NxButtonModule,
    NxFileUploaderModule,
    NxIconModule,
    NxPopoverModule,
    SkeletonComponent,
    TranslateModule,
  ],
  templateUrl: './document-info.component.html',
})
export class DocumentInfoComponent implements OnInit {
  readonly dui = input.required<string>();
  private readonly documentService: DocumentService = inject(DocumentService);
  protected isLoading = false;
  protected isPolling = false;

  protected displayedDocument: DocumentMetadata | undefined;

  get statusConfig() {
    if (!this.displayedDocument) {
      return undefined;
    }
    const documentStatusCode = this.displayedDocument.documentStatusCode;
    return documentStatusMapping.find((mapping) => mapping.statuses.includes(documentStatusCode));
  }

  get formattedCreationDate() {
    return formatDate(this.displayedDocument?.creationDateTime);
  }

  protected async downloadDocument(documentUid: string, fileName: string) {
    await this.documentService.downloadDocument(documentUid, fileName);
  }

  protected readonly DocumentStatus = DocumentStatus;
  protected readonly formatDate = formatDate;
  protected readonly documentStatusesWithAvailableDownload = documentStatusesWithAvailableDownload;

  ngOnInit(): void {
    this.isLoading = true;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.pollDocument().finally(() => {
      this.isLoading = false;
    });
  }

  private async pollDocument() {
    const maxTries = 4;
    const delayFactor = environment.pollingDelayFactor || 500;
    let isDocumentPending = true;
    this.isPolling = true;
    for (let i = 1; i <= maxTries && isDocumentPending; i++) {
      await timer(i * delayFactor);
      this.displayedDocument = await this.documentService.getDocumentMetadata(this.dui());
      this.isLoading = false;
      isDocumentPending = pendingDocumentStatuses.includes(this.displayedDocument?.documentStatusCode);
    }
    this.isPolling = false;
  }
}
